// The client environment's front door (#10718).
//
// Opening a client sends the tab to `/client-environment/enter#code=…` here.
// The code is single-use and good for a minute, and rides in the fragment so
// no server ever logs it. It is taken out of the address into memory as the
// page boots (`captureEntryLink`, before error reporting starts), so neither
// a trace nor the address bar carries it, whether or not the exchange
// succeeds. This page trades it, with the staff member's own signed-in
// session, for the collaborator's bearer token, keeps
// the token for this browser's client tabs, marks this tab as one of them,
// and opens the app (at `next` when the link carries one). The exchange is
// a react-query read keyed by the code, so React's development double-mount
// and any re-render share one request: a second request would find the code
// already spent.
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BASE_API_URL_V2 } from "@/constants";
import {
  capturedEntryLink,
  captureEntryLink,
  type EntryLink,
  markTabForClient,
  safeNextPath,
  setClientEnvironmentToken,
} from "@/services/clientEnvironment";
import { getSessionToken } from "@/services/sessionToken";
import ClientEnvironmentNotice from "./ClientEnvironmentNotice";

export const EXCHANGE_URL = `${BASE_API_URL_V2}/api/v2/staff/grants/exchange`;

/** Why an exchange did not produce a token. `signed-out`: this browser has
 * no Finpilot session, and the server redeems a code only for the signed-in
 * person who opened its grant. */
type ExchangeFailure = "spent" | "signed-out" | "unavailable";

class ExchangeError extends Error {
  constructor(readonly reason: ExchangeFailure) {
    super(reason);
  }
}

interface Exchanged {
  token: string;
  grantId: number;
  organizationId: string;
  organizationName: string;
}

async function exchange(code: string): Promise<Exchanged> {
  // A plain fetch, carrying the staff member's own signed-in session: this
  // page always runs at home, and the server redeems a code only for the
  // person who opened its grant (#10718 audit), so a link sent to someone
  // else's browser opens nothing there. A tab with no session sends none and
  // is refused. No abort signal: redeeming the code is not repeatable, so a
  // remount must wait for this request rather than cancel it and spend the
  // code twice.
  const session = getSessionToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (session) headers.Authorization = `Bearer ${session}`;
  let response: Response;
  try {
    response = await fetch(EXCHANGE_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({ code }),
    });
  } catch {
    throw new ExchangeError("unavailable");
  }
  if (response.status === 404) throw new ExchangeError("spent");
  if (response.status === 401) throw new ExchangeError("signed-out");
  if (!response.ok) throw new ExchangeError("unavailable");
  const body = (await response.json()) as {
    token?: string;
    grant?: { id: number; organization_id: string; organization_name: string };
  };
  if (!body.token || !body.grant) throw new ExchangeError("unavailable");
  return {
    token: body.token,
    grantId: body.grant.id,
    organizationId: body.grant.organization_id,
    organizationName: body.grant.organization_name,
  };
}

const NOTICES: Record<ExchangeFailure, { title: string; body: string }> = {
  spent: {
    title: "This link has expired or was already used",
    body: "Links into a client environment work once, for a minute. Open the client again from Finpilot.",
  },
  "signed-out": {
    title: "This browser is not signed in to Finpilot",
    body: "Sign in to Finpilot first, then open the link again.",
  },
  unavailable: {
    title: "The client environment could not be opened",
    body: "Open the client again from Finpilot.",
  },
};

interface ClientEnvironmentEnterProps {
  /** Opens the app at `path` once the token is kept; replaced in stories.
   * A full load, so the code held in memory goes with the page. */
  onEntered?: (path: string) => void;
}

const openApp = (path: string): void => {
  window.location.replace(path);
};

const ClientEnvironmentEnter: React.FC<ClientEnvironmentEnterProps> = ({
  onEntered = openApp,
}) => {
  // Boot has normally taken the link already. A link that reached this page
  // without a load (the address edited in a tab already on it) is taken here,
  // so the code never outlives the mount in the address either way.
  const [link] = useState<EntryLink | null>(() => {
    captureEntryLink();
    return capturedEntryLink();
  });
  const code = link?.code ?? "";
  const next = safeNextPath(link?.next) ?? "/";
  const result = useQuery<Exchanged, Error>({
    queryKey: ["client-environment-exchange", code],
    queryFn: () => exchange(code),
    enabled: code.length > 0,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (result.data) {
      setClientEnvironmentToken(result.data.token, {
        grantId: result.data.grantId,
        organizationId: result.data.organizationId,
        organizationName: result.data.organizationName,
      });
      markTabForClient();
      onEntered(next);
    }
  }, [result.data, onEntered, next]);

  if (code.length === 0 || result.error) {
    const reason =
      code.length === 0
        ? "spent"
        : ((result.error as ExchangeError | null)?.reason ?? "unavailable");
    const notice = NOTICES[reason];
    return (
      <ClientEnvironmentNotice title={notice.title}>
        {notice.body}
      </ClientEnvironmentNotice>
    );
  }

  // Nothing while the code is traded: the exchange is quick, and a staff
  // link into a client passes through here on its way to the content, so
  // the tab should read as the page loading (slice 4), not as a gate.
  return null;
};

export default ClientEnvironmentEnter;
